import React, { useState, useRef } from "react";
import { Button, Card, Dropdown } from "react-bootstrap";
import ShareSites from "./ShareSites";
import EditSite from "./EditSite";
import ConfirmationPopup from "components/common/ConfirmationPopup";
import SiteService from "services/SiteService";
import {
  SITE_DELETE_ERROR,
  ARE_YOU_SURE_DELETE_SITE,
  ARE_YOU_SURE_STOP_SHARE_SITE,
  SITE_STOP_SHARE_ERROR,
  ERROR_UPDATING_SHARE_DETAILS,
  SITE_SHARING_HAS_BEEN_STOPPED,
  SITE_HAS_BEEN_DELETED_SUCCESSFULLY,
} from "configurations/MessageConstants";
import Notes from "components/common/notes/Notes";
import { NOTES_TYPES, SHARE_TYPES } from "configurations/Constants";
import { setSelectedSite, setSites } from "reduxStore/slices/siteSlice";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const SiteCard = ({
  type,
  site,
  onSiteClick,
  onShareSite,
  onEditSite,
  onDeleteSite,
  onStopShareSite,
  onShowNotes,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const sites = useSelector((state) => state.site.sites);
  const [showShareSite, setShowShareSite] = useState(false);
  const [showEditSite, setShowEditSite] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [showStopShareConfirmation, setShowStopShareConfirmation] =
    useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const dropdownRef = useRef(null);
  const shareButtonRef = useRef(null);
  const isSharedViewOnly = site.isShared && !site.editor;

  const handleDeleteClick = () => {
    if (onDeleteSite) {
      onDeleteSite(site);
    } else {
      setShowDeleteConfirmation(true);
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await SiteService.deleteSite(site.id);
      setShowDeleteConfirmation(false);
      dispatch(
        setSites(sites.filter((deletedSite) => deletedSite.id !== site.id))
      );
      dispatch(setSelectedSite(null));
      navigate("/sites", { state: { fromBack: true } });
      toast.success(SITE_HAS_BEEN_DELETED_SUCCESSFULLY);
    } catch (error) {
      console.error(SITE_DELETE_ERROR, error);
      toast.error(error.response.data.detail);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStopShareClick = () => {
    if (onStopShareSite) {
      onStopShareSite(site);
    } else {
      setShowStopShareConfirmation(true);
    }
  };

  const handleConfirmStopShare = async () => {
    setIsDeleting(true);
    try {
      await SiteService.updateShare(site.sharingId, site.id, {
        active: false,
      });
      setShowStopShareConfirmation(false);
      dispatch(
        setSites(sites.filter((stoppedSite) => stoppedSite.id !== site.id))
      );
      dispatch(setSelectedSite(null));
      navigate("/sites", { state: { fromBack: true } });
      toast.success(SITE_SHARING_HAS_BEEN_STOPPED);
    } catch (error) {
      console.error(SITE_STOP_SHARE_ERROR, error);
      toast.error(ERROR_UPDATING_SHARE_DETAILS);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleShareClick = () => {
    if (onShareSite) {
      onShareSite(site);
    } else {
      setShowShareSite(true);
    }
  };

  const handleEditClick = (e) => {
    if (onEditSite) {
      onEditSite(site);
    } else {
      setShowEditSite(true);
    }
  };
  const handleShowNotes = (e) => {
    if (onShowNotes) {
      onShowNotes(site);
    } else {
      setShowNotes(true);
    }
  };

  const handleSiteCardClick = (e) => {
    if (
      dropdownRef.current?.contains(e.target) ||
      shareButtonRef.current?.contains(e.target)
    )
      return;
    onSiteClick(site);
  };

  return (
    <>
      <Card key={site.id} className={type} onClick={handleSiteCardClick}>
        <Card.Body className="p-2">
          <div className="d-flex justify-content-between">
            <div className="text-truncate">
              <div className="text-truncate">{site.name}</div>
              <small className="text-muted d-flex gap-1 align-items-center">
                <span className="text-truncate">{site.farmName}</span>|
                <span className="text-truncate">{site.businessName}</span>
              </small>
            </div>
            <div className="ms-2 d-flex flex-column align-items-end">
              <small className="text-muted text-nowrap">
                {site.totalArea} Acres
              </small>
              <div className="d-flex flex-row gap-2 mt-1 justify-content-end">
                {!site.sharedBy && (
                  <Button
                    ref={shareButtonRef}
                    variant="outline-dark"
                    size="sm"
                    className="z-3"
                    onClick={handleShareClick}
                    aria-label="Share Site"
                  >
                    <i className="fa fa-share-alt" aria-hidden="true"></i>
                  </Button>
                )}
                <Dropdown ref={dropdownRef} align="end">
                  <Dropdown.Toggle
                    variant="outline-dark"
                    className="no-caret"
                    size="sm"
                    id={`dropdown-${site.name}`}
                    aria-label="Site actions"
                  >
                    <i className="fa fa-ellipsis-h" aria-hidden="true"></i>
                  </Dropdown.Toggle>
                  <Dropdown.Menu style={{ minWidth: "fit-content" }}>
                    <Dropdown.Item onClick={handleEditClick}>
                      {isSharedViewOnly ? (
                        <>
                          <i className="fa fa-eye me-2"></i>View
                        </>
                      ) : (
                        <>
                          <i className="fa fa-edit me-2"></i>Edit
                        </>
                      )}
                    </Dropdown.Item>
                    {!isSharedViewOnly && (
                      <Dropdown.Item onClick={handleShowNotes}>
                        <i
                          className="fa fa-sticky-note me-2"
                          aria-hidden="true"
                        ></i>
                        Notes
                      </Dropdown.Item>
                    )}
                    {!site.sharedBy ? (
                      <Dropdown.Item onClick={handleDeleteClick}>
                        <i className="fa fa-trash me-2" aria-hidden="true"></i>
                        Delete
                      </Dropdown.Item>
                    ) : (
                      <Dropdown.Item onClick={handleStopShareClick}>
                        <i
                          className="fa fa-share-alt me-2"
                          aria-hidden="true"
                        ></i>
                        Stop
                      </Dropdown.Item>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </div>
        </Card.Body>
        {site.sharedBy && (
          <>
            <div className="text-end border-top">
              <small className="me-2 shared-by-site-label">
                Shared by <strong>{site.sharedByName}</strong>
              </small>
            </div>
          </>
        )}
      </Card>
      {!onShareSite && showShareSite && (
        <ShareSites
          type={SHARE_TYPES.SINGLE}
          selected={[site]}
          show={showShareSite}
          closeShare={() => setShowShareSite(false)}
        />
      )}
      {!onEditSite && showEditSite && (
        <EditSite
          site={site}
          show={showEditSite}
          setShowEditSite={setShowEditSite}
        />
      )}
      {!onDeleteSite && (
        <ConfirmationPopup
          show={showDeleteConfirmation}
          title="Delete Site"
          message={
            <span className="text-muted">
              {ARE_YOU_SURE_DELETE_SITE} <strong>{site.name}</strong>?
            </span>
          }
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteConfirmation(false)}
          confirmText="Delete"
          cancelText="Cancel"
          isDeleting={isDeleting}
        />
      )}
      {!onStopShareSite && (
        <ConfirmationPopup
          show={showStopShareConfirmation}
          title="Stop Share Site"
          message={
            <span className="text-muted">
              {ARE_YOU_SURE_STOP_SHARE_SITE} <strong>{site.name}</strong>?
            </span>
          }
          onConfirm={handleConfirmStopShare}
          onCancel={() => setShowStopShareConfirmation(false)}
          confirmText="Stop"
          cancelText="Cancel"
          isDeleting={isDeleting}
        />
      )}
      {!onShowNotes && showNotes && (
        <Notes
          id={site.id}
          type={NOTES_TYPES.SITE}
          name={site.name}
          show={showNotes}
          handleClose={() => setShowNotes(false)}
        />
      )}
    </>
  );
};

export default SiteCard;
